import React, {memo, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {Popconfirm, Button} from 'antd';
import {CaretDownOutlined, CaretRightOutlined, DeleteOutlined} from '@ant-design/icons';
import {Link, useRouteMatch} from 'react-router-dom';
import classnames from 'classnames';

import {OpenApiContext} from '../../openapi-context';
import {AppContext} from '../../state';

// Hand-rolled tree inspired by Stoplight Elements' TableOfContents:
// recursive rendering, depth-indent via paddingLeft, chevron toggle per
// group, active-item background tint. No antd Tree wrapper, no third-party
// virtualization library. Fast because each row is ~5 DOM nodes, and
// content-visibility (in styles.scss) defers paint for off-screen rows.

const ROW_INDENT_PX = 16;
const ROW_BASE_PAD_PX = 8;

function splitSchemaName(name) {
    return String(name || '').split('.').filter(Boolean);
}

// Same tree build as before: dot-split → nested node objects keyed by path.
// `model` is attached to a node only if that exact path has a real schema
// (synthetic parents stay as folder-only nodes).
function buildTree(schemas) {
    const root = {children: new Map()};

    for (const schema of schemas) {
        const segments = splitSchemaName(schema.name);
        let cursor = root;
        const path = [];
        for (const seg of segments) {
            path.push(seg);
            if (!cursor.children.has(seg)) {
                cursor.children.set(seg, {segment: seg, path: path.join('.'), children: new Map()});
            }
            cursor = cursor.children.get(seg);
        }
        cursor.model = schema;
    }

    const toNode = (n) => ({
        title: n.segment,
        path: n.path,
        model: n.model,
        children: n.children.size > 0 ? [...n.children.values()].map(toNode) : [],
    });

    return [...root.children.values()].map(toNode);
}

function collectAllPaths(nodes, acc = []) {
    for (const n of nodes) {
        acc.push(n.path);
        if (n.children.length) collectAllPaths(n.children, acc);
    }
    return acc;
}

// Return the set of ancestor paths for a given leaf path.
// e.g. "bills.allocations.orderAllocations" -> ["bills", "bills.allocations"]
function ancestorPathsOf(path) {
    const segs = splitSchemaName(path);
    const out = [];
    for (let i = 1; i < segs.length; i++) {
        out.push(segs.slice(0, i).join('.'));
    }
    return out;
}

// Single row component. React.memo so changing openPaths / activePath only
// re-renders rows whose own props changed, not every row.
const TreeRow = memo(function TreeRow({
    node,
    depth,
    isOpen,
    isActive,
    hasChildren,
    onToggle,
    onNavigate,
    onDelete,
    params,
    version,
}) {
    const pad = ROW_BASE_PAD_PX + depth * ROW_INDENT_PX;
    const isFolder = !node.model;

    return (
        <div
            className={classnames('modelTree__row', {
                'is-active': isActive,
                'is-folder': isFolder,
            })}
            style={{paddingLeft: pad}}>
            {hasChildren ? (
                <span className="modelTree__chevron" onClick={onToggle} role="button" aria-label={isOpen ? 'collapse' : 'expand'}>
                    {isOpen ? <CaretDownOutlined /> : <CaretRightOutlined />}
                </span>
            ) : (
                <span className="modelTree__chevron modelTree__chevron--spacer" />
            )}

            {isFolder ? (
                <span className="modelTree__label" onClick={hasChildren ? onToggle : undefined}>
                    {node.title}
                </span>
            ) : (
                <Link
                    className="modelTree__label modelTree__label--link"
                    onClick={onNavigate}
                    to={`/${params.env}/standards/${params.category}/${params.key}/${version}/models/${node.model.key}`}>
                    {node.title}
                </Link>
            )}

            {!isFolder && (
                <Popconfirm okType="default" title={`Delete model ${node.model.name} ?`} onConfirm={() => onDelete(node.model)}>
                    <Button
                        className="modelTree__delete"
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={(e) => e.stopPropagation()}
                    />
                </Popconfirm>
            )}
        </div>
    );
});

// Branch: one row plus its expanded children. Splitting row from branch
// lets TreeRow be memoized cleanly (its props are flat primitives) while
// Branch holds the recursion.
function TreeBranch({node, depth, openPaths, activePath, onTogglePath, onNavigate, onDelete, params, version}) {
    const isOpen = openPaths.has(node.path);
    const hasChildren = node.children.length > 0;

    const handleToggle = useCallback(() => onTogglePath(node.path), [node.path, onTogglePath]);

    return (
        <>
            <TreeRow
                node={node}
                depth={depth}
                isOpen={isOpen}
                isActive={activePath === node.path}
                hasChildren={hasChildren}
                onToggle={handleToggle}
                onNavigate={onNavigate}
                onDelete={onDelete}
                params={params}
                version={version}
            />
            {isOpen && hasChildren && node.children.map((child) => (
                <TreeBranch
                    key={child.path}
                    node={child}
                    depth={depth + 1}
                    openPaths={openPaths}
                    activePath={activePath}
                    onTogglePath={onTogglePath}
                    onNavigate={onNavigate}
                    onDelete={onDelete}
                    params={params}
                    version={version}
                />
            ))}
        </>
    );
}

// Per-spec localStorage key. Open-node state is scoped to the specific spec
// under a specific workgroup so switching specs doesn't leak expansion.
function storageKeyFor(params) {
    if (!params || !params.env || !params.category || !params.key) return null;
    return `acuwize.modelTree.open.${params.env}.${params.category}.${params.key}`;
}

function loadOpenPaths(key) {
    if (!key || typeof window === 'undefined' || !window.localStorage) return new Set();
    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return new Set();
        const arr = JSON.parse(raw);
        return new Set(Array.isArray(arr) ? arr : []);
    } catch {
        return new Set();
    }
}

function saveOpenPaths(key, set) {
    if (!key || typeof window === 'undefined' || !window.localStorage) return;
    try {
        window.localStorage.setItem(key, JSON.stringify([...set]));
    } catch {
        // quota or serialization error — silently drop; tree still works in-memory
    }
}

export function ModelIndex({onDeleteModel, params, currentHash, filter, checkMobileView}) {
    const {state} = useContext(OpenApiContext);
    const {dispatch} = useContext(AppContext);
    const match = useRouteMatch('/:env/standards/:category/:key/:version?/:view?/:modelKey?');

    const [schemas, setSchemas] = useState([]);

    // userOpenPaths: what the user explicitly opened (and what we persist).
    // Filter-driven expand-all does NOT mutate this; it only overrides the
    // render. That way, clearing the filter restores exactly what the user
    // had open before the filter was typed.
    const storageKey = storageKeyFor(params);
    const [userOpenPaths, setUserOpenPaths] = useState(() => loadOpenPaths(storageKey));

    // Reload persisted state when the spec changes (nav between specs).
    useEffect(() => {
        setUserOpenPaths(loadOpenPaths(storageKey));
    }, [storageKey]);

    // Persist on every change. localStorage writes are synchronous and
    // fast for a few-KB payload; no need to debounce.
    useEffect(() => {
        saveOpenPaths(storageKey, userOpenPaths);
    }, [storageKey, userOpenPaths]);

    useEffect(() => {
        if (state.schemas) setSchemas(state.schemas(filter));
    }, [state.schemas, filter, currentHash]);

    const treeData = useMemo(() => buildTree(schemas), [schemas]);

    // URL-driven: when navigating to a deep schema, auto-expand its ancestors
    // so the active item is visible. Ancestors get persisted too — feels
    // natural to the user: "I jumped to X, the tree opened to show it, and
    // that state sticks next time".
    const activePath = useMemo(() => {
        const urlModelKey = match?.params?.modelKey;
        if (!urlModelKey || match?.params?.view !== 'models') return null;
        const hit = schemas.find((s) => s.key === urlModelKey);
        return hit ? hit.name : null;
    }, [match, schemas]);

    useEffect(() => {
        if (!activePath) return;
        const ancestors = ancestorPathsOf(activePath);
        if (ancestors.length === 0) return;
        setUserOpenPaths((prev) => {
            let changed = false;
            const next = new Set(prev);
            for (const a of ancestors) {
                if (!next.has(a)) {
                    next.add(a);
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [activePath]);

    // When a filter is active, we render everything expanded so matches are
    // always visible. effectiveOpenPaths composes user state + filter override;
    // the filter does not mutate userOpenPaths.
    const effectiveOpenPaths = useMemo(() => {
        if (filter) return new Set(collectAllPaths(treeData));
        return userOpenPaths;
    }, [filter, userOpenPaths, treeData]);

    const onTogglePath = useCallback((path) => {
        setUserOpenPaths((prev) => {
            const next = new Set(prev);
            if (next.has(path)) next.delete(path);
            else next.add(path);
            return next;
        });
    }, []);

    const onNavigate = useCallback(() => {
        if (checkMobileView) checkMobileView();
        dispatch({type: 'clear-breadcrumb'});
    }, [checkMobileView, dispatch]);

    const onDelete = useCallback(
        (model) => {
            onDeleteModel(model);
        },
        [onDeleteModel],
    );

    const version = params.version || 'latest';

    return (
        <div className="modelTree">
            {treeData.map((node) => (
                <TreeBranch
                    key={node.path}
                    node={node}
                    depth={0}
                    openPaths={effectiveOpenPaths}
                    activePath={activePath}
                    onTogglePath={onTogglePath}
                    onNavigate={onNavigate}
                    onDelete={onDelete}
                    params={params}
                    version={version}
                />
            ))}
        </div>
    );
}
